import { useCallback, useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';

import { IContentTypeValue, UserBudgetSummaryDocument } from 'graphql/types';
import { PageTitle } from 'common/components/PageTitle';
import { useApolloError } from 'common/hooks/useApolloError';
import { getUrl } from 'route/utils/getUrl';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { IUserProfileSectionProps } from 'user/types';
import { usePermission } from 'user/hooks/usePermissions';
import { PendingBudgetRequestsCard } from 'budget/components/PendingBudgetRequestsCard';
import { UserBudgetRequests } from 'budget/components/UserBudgetRequests/UserBudgetRequests';

export const UserBudget = ({ id, name }: IUserProfileSectionProps) => {
    const [translate] = useTranslation();

    const navigate = useNavigate();
    const location = useLocation();

    const portfolioBudgetMatch = useRouteMatch('PORTFOLIO_BUDGET');
    const budgetRequestCreateMatch = useRouteMatch('BUDGET_REQUEST_CREATE');
    const budgetRequestEditMatch = useRouteMatch('BUDGET_REQUEST_EDIT');

    const isOwnPortfolio =
        !!portfolioBudgetMatch ||
        !!budgetRequestCreateMatch ||
        !!budgetRequestEditMatch;

    const { showApolloError } = useApolloError();

    const { data, loading, error } = useQuery(UserBudgetSummaryDocument, {
        variables: { userId: id },
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const { hasPermission: canRequestBudget } = usePermission({
        category: 'createPortfolioBudgetRequest',
        contentType: IContentTypeValue.User,
        targetId: id,
    });

    const handleRequestBudget = useCallback(() => {
        navigate(`${getUrl('BUDGET_REQUEST_CREATE')}${location.search}`);
    }, [location.search, navigate]);

    return (
        <>
            <PageTitle>{`${translate('userProfileTabs.PORTFOLIO_BUDGET')} - ${name}`}</PageTitle>

            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    mb: 4,
                }}
            >
                <Box sx={{ gridColumnStart: 2 }}>
                    <PendingBudgetRequestsCard
                        count={data?.userBudgetSummary.pendingCount ?? 0}
                        loading={loading}
                        onRequestBudget={
                            isOwnPortfolio && canRequestBudget
                                ? handleRequestBudget
                                : undefined
                        }
                    />
                </Box>
            </Box>

            <UserBudgetRequests userId={id} />
        </>
    );
};
